import { useGraphQLClient } from "@package/graphql/react";
import { useNetworkStatus } from "@package/react/hooks";
import { useQueryClient } from "@tanstack/react-query";
import type { TStatus } from "@package/grid-utils";
import { logger } from "@artis/logger";
import { useEffect } from "react";

import {
  useAllPageProducts,
  type ProductInfo,
  type TGridSettings,
  type TProduct,
  type TProductConfigs,
} from "../../../data";
import { useProductConfigsCache } from "../../../data/cached/preload";
import type { TAlert } from "../../../data/alerts/queries";
import { GridProductsByIds, PackageFolderAccessQuery, ProductInfoFragment } from "../../../data/products/queries";
import { readFragment } from "../../../graphql";
import {
  accessAlertFromOffStreamClassification,
  classifyOffStreamCurve,
  folderAccessFromPermissions,
  type CurveFolderDebugPayload,
  type FolderAccess,
  type ProductChangeAlert,
} from "../lib/features/product-alerts";
import { useFeatureFlags } from "../../../context/flags";
import { attachLivePriceTracing } from "../../../context/o11y/live-prices-trace";
import { debugAtomStore, debugOptionsAtom } from "../main/debug";
import { createLivePriceConsoleLogger } from "../main/trace-console";
import { livePricesConfigFromFlag } from "../lib/sources/live-prices/connection";
import { useCalcWorker, usePauseCalcs } from "./hooks";
import { useUserId } from "../../../context/auth/hooks";

/**
 * Starts calc worker and makes it available to children
 * The reason for the props is that it is used in the webapp grid and excel addin
 * in the excel addin we dont want cell and product alerts
 */
export const CalcWorkerProvider = ({
  userId,
  username,
  token,
  pageId,
  children,
  gridSettings,
  onCellAlerts,
  onFailedManualEdits,
  onProductChangeAlert,
}: {
  userId?: string;
  username?: string;
  token?: string;
  pageId?: string;
  children: React.ReactNode;
  gridSettings: Pick<TGridSettings, "status_map" | "eod_date">;
  onCellAlerts?: (alerts: TAlert[]) => void;
  onProductChangeAlert?: (products: ProductChangeAlert[]) => void;
  onFailedManualEdits?: (
    edits: {
      product: string;
      result: number | null;
      rowId: string;
    }[],
  ) => void;
}) => {
  const [stopped] = usePauseCalcs();
  const worker = useCalcWorker();
  const qry = useQueryClient();
  const graphql = useGraphQLClient();
  const folioUserId = useUserId();
  const pageProducts = useAllPageProducts(folioUserId);
  const online = useNetworkStatus();
  const cache = useProductConfigsCache();
  const [wsV2Flag] = useFeatureFlags("live-prices-ws-v2");

  // Must run before the start effect so the config reaches the bus before the runner connects.
  useEffect(() => {
    worker.send({ type: "live-prices-config", data: livePricesConfigFromFlag(wsV2Flag) });
  }, [worker, wsV2Flag]);

  useEffect(() => {
    if (stopped || !online) worker.stop();
    else worker.start();
  }, [worker, stopped, online]);

  useEffect(() => attachLivePriceTracing(worker), [worker]);
  useEffect(
    () =>
      worker.trace.onStage(
        createLivePriceConsoleLogger({ enabled: () => debugAtomStore.get(debugOptionsAtom).log_traces }),
      ),
    [worker],
  );

  useEffect(() => {
    return worker.onEvent((event) => {
      switch (event.type) {
        case "configs-changed":
          qry.invalidateQueries({ queryKey: ["configs"] });
          break;
        case "products-changed":
          qry.invalidateQueries({ queryKey: ["products"] });
          break;
        case "alerts-changed":
          qry.invalidateQueries({ queryKey: ["alerts"] });
          break;
        // Both alert cases need a handler: this provider also runs where alerts have nowhere to go
        // (Curve Management, the Excel add-in), and acking there would commit the folder baseline
        // in the shared store, so the Market session would never report those changes.
        case "curve-folder-debug":
          if (folioUserId && onProductChangeAlert) {
            void handleCurveFolderDebug(event.data, {
              qry,
              graphql,
              folioUserId,
              cache,
              worker,
              onProductChangeAlert,
            });
          }
          break;
        case "product-alerts":
          if (onProductChangeAlert) {
            onProductChangeAlert(event.data.alerts);
            // Confirms the batch reached the (persisted) sidebar so the worker can commit the
            // folder baseline it was detected against — without this the worker re-sends on boot.
            worker.send({ type: "product-alerts-ack", data: event.data.ackId });
          }
          break;
        case "cell-alerts":
          onCellAlerts?.(event.data);
          break;
        case "failed-manual-edits":
          onFailedManualEdits?.(event.data);
          break;
      }
    });
  }, [worker, qry, graphql, folioUserId, cache, onProductChangeAlert, onCellAlerts, onFailedManualEdits]);

  useEffect(() => {
    if (gridSettings) {
      worker.send({ type: "status-map", data: { ...(gridSettings?.status_map as Record<string, TStatus>) } });
      worker.send({ type: "eod-date", data: gridSettings.eod_date ?? null });
    }
  }, [worker, gridSettings]);

  useEffect(() => {
    if (!userId) return;
    worker.send({ type: "user", data: { id: userId, username: username ?? "" } });
  }, [worker, userId, username]);

  useEffect(() => {
    if (!token) return;
    worker.send({ type: "access-token", data: token });
  }, [worker, token]);

  useEffect(() => {
    if (!pageProducts.data) return;
    const rows = pageProducts.data.filter((p) => !pageId || p.page_id === pageId);
    const data = rows
      .map((p) => {
        if (!p?.product_id || !p?.column_field_selector) return null;
        return {
          product_id: p.product_id,
          column_field_selector: p.column_field_selector,
        };
      })
      .filter(Boolean);
    worker.send({ type: "page-products", data });
  }, [worker, pageProducts.data, pageId]);

  return children;
};

type ProductSyncCtx = {
  qry: ReturnType<typeof useQueryClient>;
  graphql: ReturnType<typeof useGraphQLClient>;
  folioUserId: string;
  cache: ReturnType<typeof useProductConfigsCache>;
  worker: ReturnType<typeof useCalcWorker>;
};

const dropProductsFromMainThread = (
  ctx: Pick<ProductSyncCtx, "cache" | "qry" | "folioUserId" | "worker">,
  ids: string[],
) => {
  if (ids.length === 0) return;
  ctx.cache.removeProducts(ids);
  ctx.qry.setQueryData<ProductInfo>(["products", ctx.folioUserId], (old) => {
    if (!old) return old;
    let changed = false;
    const next = { ...old };
    for (const id of ids) {
      if (id in next) {
        delete next[id];
        changed = true;
      }
    }
    return changed ? next : old;
  });
  ctx.qry.setQueryData<TProductConfigs>(["configs", ctx.folioUserId], (old) => {
    if (!old) return old;
    let changed = false;
    const next = { ...old };
    for (const id of ids) {
      if (id in next) {
        delete next[id];
        changed = true;
      }
    }
    return changed ? next : old;
  });
  clearGridValues(ctx, ids);
};

const clearGridValues = (ctx: Pick<ProductSyncCtx, "worker">, ids: string[]) => {
  if (ids.length === 0) return;
  ctx.worker.grid.applyUpdate({
    type: "grid:update:root",
    data: { update: [], remove: ids },
  });
};

async function handleCurveFolderDebug(
  payloads: CurveFolderDebugPayload[],
  ctx: ProductSyncCtx & {
    onProductChangeAlert?: (products: ProductChangeAlert[]) => void;
  },
) {
  // Losing a folder drops every one of its curves off the page at once, so both lookups are
  // shared across the batch rather than repeated per curve.
  const foundById = await fetchProductsByIds(
    ctx,
    payloads.filter((payload) => payload.accessChangeReason !== "curve-moved").map((payload) => payload.productId),
  );
  const folderAccessByPackageId = new Map<string, Awaited<ReturnType<typeof fetchOriginalFolderAccess>>>();
  const missingFromLiveQuery: string[] = [];

  for (const payload of payloads) {
    const lastKnown = {
      packageId: payload.lastKnownFolder?.packageId ?? null,
      folderName: payload.lastKnownFolder?.folderName ?? null,
      folderAccess: payload.folderAccessChange?.from ?? "none",
    };

    let found: TProduct | undefined;
    let lookupSource: "main-thread-cache" | "by-id-query" | "none" = "none";

    if (payload.accessChangeReason !== "curve-moved") {
      // The lookup failed outright: classifying without it would misreport the reason.
      if (!foundById) continue;

      found = foundById.get(payload.productId);
      if (found) {
        lookupSource = "by-id-query";
      } else {
        // Do not fall back to react-query/IDB: those rows lag the live query and make a move
        // into an inaccessible folder look like a transient stream drop (no notification, stale grid).
        missingFromLiveQuery.push(payload.productId);
      }
    }

    if (lastKnown.packageId !== null && !folderAccessByPackageId.has(lastKnown.packageId)) {
      folderAccessByPackageId.set(lastKnown.packageId, await fetchOriginalFolderAccess(ctx, lastKnown.packageId));
    }
    const originalFolder = lastKnown.packageId === null ? undefined : folderAccessByPackageId.get(lastKnown.packageId);
    const classified =
      payload.accessChangeReason === "curve-moved"
        ? {
            accessChangeReason: "curve-moved" as const,
            lookupSource: "worker-recent-move" as const,
            originalFolderAccess: originalFolder?.access,
            originalFolderName: originalFolder?.name ?? lastKnown.folderName,
            note: payload.note ?? "Folder changed, then row left the bulk stream.",
          }
        : classifyOffStreamCurve(lastKnown, found, lookupSource, originalFolder);

    const accessAlert = accessAlertFromOffStreamClassification(payload, classified, found ?? undefined);
    if (accessAlert) ctx.onProductChangeAlert?.([accessAlert]);
  }

  dropProductsFromMainThread(ctx, missingFromLiveQuery);
}

/** Null signals the query itself failed, which is different from it finding no rows. */
async function fetchProductsByIds(
  ctx: { graphql: ReturnType<typeof useGraphQLClient>; folioUserId: string },
  productIds: string[],
): Promise<Map<string, TProduct> | null> {
  const ids = [...new Set(productIds)];
  if (ids.length === 0) return new Map();

  try {
    const res = await ctx.graphql.execute(GridProductsByIds, { ids, folioUser: ctx.folioUserId });
    const rows = readFragment(ProductInfoFragment, res.data?.product) ?? [];
    return new Map(rows.map((row) => [row.id, row] as const));
  } catch (error) {
    logger.warn("Curve folder lookup failed, skipping access classification", { ids, error });
    return null;
  }
}

async function fetchOriginalFolderAccess(
  ctx: { graphql: ReturnType<typeof useGraphQLClient>; folioUserId: string },
  packageId: string | null,
): Promise<{ access: FolderAccess; name: string | null } | undefined> {
  if (!packageId) return undefined;

  const numericPackageId = Number(packageId);
  if (!Number.isFinite(numericPackageId)) return undefined;

  try {
    const res = await ctx.graphql.execute(PackageFolderAccessQuery, {
      packageId: numericPackageId,
      folioUser: ctx.folioUserId,
    });
    const pkg = res.data?.package_by_pk;
    if (!pkg) {
      return { access: "none", name: null };
    }

    return {
      access: folderAccessFromPermissions(pkg.permissions),
      name: pkg.name ?? null,
    };
  } catch {
    return undefined;
  }
}
